import { ReactComponent as DroneIcon } from '@assets/components/gauge/drone.svg';
import { ReactComponent as SelectedDroneIcon } from '@assets/components/gauge/selected-drone.svg';
import { Box, For, Icon } from '@chakra-ui/react';
import { useAppSelector } from '@hooks/redux.hooks';
import { selectActiveOrganization } from '@redux/authent/authent.selectors';
import { selectContext } from '@redux/settings/settings.selectors';
import { selectSelectedTargets, selectSpatialDrones } from '@redux/situation/situation.selectors';
import bearing from '@turf/bearing';
import { bearingToAzimuth } from '@turf/helpers';
import { getMapIdFromReplayMode, getTargetColor } from '@utils/map/map.utils';
import { getPositionOnCircle } from '@utils/sensors/camera/gauge.utils';
import { isEqual } from 'lodash';

import { Coordinates } from '@/types/commons/commons.types';

type Props = {
  replayMode: boolean;
  cameraPosition: Coordinates;
};

export default function AzimuthCompassTargets({ replayMode, cameraPosition }: Readonly<Props>) {
  const targets = useAppSelector((state) => selectSpatialDrones(state, replayMode));
  const currentContext = useAppSelector(selectContext);
  const selectedTargets = useAppSelector(
    (state) => selectSelectedTargets(state, getMapIdFromReplayMode(replayMode, currentContext)),
    isEqual,
  );
  const organization = useAppSelector(selectActiveOrganization);

  return (
    <Box position="absolute" width="100%" height="100%" top={0}>
      <For each={targets.filter((target) => !!target.lastPosition.location?.position)}>
        {(target) => {
          const targetCoordinates = target.lastPosition.location!.position!;
          const targetPosition = [targetCoordinates.longitude, targetCoordinates.latitude];
          const angle = +bearingToAzimuth(
            bearing([cameraPosition.longitude, cameraPosition.latitude], targetPosition),
          ).toFixed(1);
          const position = getPositionOnCircle(angle - 90, 90, { x: 90, y: 90 }, 0);
          const isSelected = selectedTargets.some((t) => t.id === target.id);
          return (
            <Icon
              as={isSelected ? SelectedDroneIcon : DroneIcon}
              color={getTargetColor(target, organization)}
              zIndex={isSelected ? 1 : 0}
              key={target.id}
              width={isSelected ? '54px' : '24px'}
              height="24px"
              position="absolute"
              transform="translate(-12px, -12px)"
              top={`${position.y}px`}
              left={`${position.x}px`}
            />
          );
        }}
      </For>
    </Box>
  );
}
